import { supabase } from "./supabase";
import type { NivelDeAcesso } from "./admin";

// O IO da tela Admin › Permissões (000295, e desde a 000297 a ÚNICA porta de
// acesso ao painel) — ZERO regra.
//
// Quem decide o nível de alguém numa aba é `meu_nivel(área)`, no banco, e quem
// valida um ato (só o master, motivo obrigatório, o último master que consegue
// entrar) são as RPCs `perm_admin_*`. As recusas trazem a frase pronta em
// `detalhe`: traduzir `motivo` aqui criaria a segunda tabela de frases, e a que
// divergisse em silêncio seria a que ninguém lê (a mesma regra de
// lib/adminCatalogo.ts). Nenhum `select` cru: as cinco tabelas têm `revoke all`.

export type AreaDoPainel = {
  id: string;
  rotulo: string;
  /** Em que bloco do menu a aba aparece — apresentação, não permissão. */
  menu: "suporte" | "admin";
  descricao: string | null;
};

export type Pessoa = { nome: string; email: string; tem_login: boolean };

export type GrupoDePermissao = {
  slug: string;
  nome: string;
  descricao: string | null;
  /** Só as abas que o grupo tem; ausência é "nenhum". */
  concessoes: Record<string, NivelDeAcesso>;
  membros: Pessoa[];
};

export type Excecao = { nome: string; email: string; area: string; nivel: NivelDeAcesso };

export type MudancaDePermissao = {
  alvo: "concessao" | "membro" | "excecao" | "grupo" | "master";
  alvo_ref: string;
  antes: Record<string, unknown> | null;
  depois: Record<string, unknown> | null;
  por_descricao: string;
  motivo: string;
  em: string;
};

export type Matriz = {
  ok: true;
  areas: AreaDoPainel[];
  grupos: GrupoDePermissao[];
  excecoes: Excecao[];
  /** O único papel que sobrou (000297): quem está aqui passa por tudo. */
  masters: Pessoa[];
  mudancas: MudancaDePermissao[];
};

export type RespostaDePermissao = { ok: boolean; mudou?: boolean; motivo?: string; detalhe?: string; sem_login?: boolean };

/** Nulo para quem não é master — é a resposta do portão, não uma falha. */
export async function lerMatriz(): Promise<Matriz | null> {
  const { data, error } = await supabase.rpc("perm_admin_ler");
  if (error) throw error;
  return (data as Matriz | null) ?? null;
}

async function ato(nome: string, args: Record<string, unknown>): Promise<RespostaDePermissao> {
  const { data, error } = await supabase.rpc(nome, args);
  if (error) return { ok: false, detalhe: error.message };
  return (data as RespostaDePermissao) ?? { ok: false, detalhe: "o servidor não respondeu" };
}

/** `nenhum` TIRA a concessão do grupo. */
export const definirConcessao = (grupo: string, area: string, nivel: NivelDeAcesso, motivo: string) =>
  ato("perm_admin_definir", { p_grupo: grupo, p_area: area, p_nivel: nivel, p_motivo: motivo });

export const mudarMembro = (grupo: string, email: string, entra: boolean, motivo: string) =>
  ato("perm_admin_membro", { p_grupo: grupo, p_email: email, p_entra: entra, p_motivo: motivo });

/** `null` TIRA a exceção (a pessoa volta a valer pelo grupo); `nenhum` é exceção de verdade. */
export const definirExcecao = (email: string, area: string, nivel: NivelDeAcesso | null, motivo: string) =>
  ato("perm_admin_excecao", { p_email: email, p_area: area, p_nivel: nivel, p_motivo: motivo });

export const criarGrupo = (slug: string, nome: string, descricao: string, motivo: string) =>
  ato("perm_admin_criar_grupo", { p_slug: slug, p_nome: nome, p_descricao: descricao, p_motivo: motivo });

/** Nome e descrição. O identificador (`slug`) não muda: é por ele que o razão lembra o grupo. */
export const editarGrupo = (slug: string, nome: string, descricao: string, motivo: string) =>
  ato("perm_admin_editar_grupo", { p_slug: slug, p_nome: nome, p_descricao: descricao, p_motivo: motivo });

/** Membros e concessões saem junto; a fotografia do que o grupo dava fica no razão. */
export const excluirGrupo = (slug: string, motivo: string) =>
  ato("perm_admin_excluir_grupo", { p_slug: slug, p_motivo: motivo });

/** Dá (`true`) ou tira (`false`) o master. O banco recusa tirar o último que consegue entrar. */
export const definirMaster = (email: string, master: boolean, motivo: string) =>
  ato("perm_admin_master", { p_email: email, p_master: master, p_motivo: motivo });
